import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import RootLayout from './components/layout/RootLayout';
import Spinner from './components/common/Spinner';
import { checkAuthLoader, checkNotAuthLoader } from './utils/auth';
import { capabilityLoader } from './utils/authSession';

// Gates read server-computed capabilities (GET auth/session/), never the role in storage.
const requireAdminConsole = capabilityLoader('view_admin_console');
const requireEditDepartments = capabilityLoader('edit_departments');
const requirePlatformAdmin = capabilityLoader('platform_admin');

const Signin = lazy(() => import('./pages/Signin'));
const Signup = lazy(() => import('./pages/Signup'));
const Home = lazy(() => import('./pages/Home'));
const TermsOfService = lazy(() => import('./pages/legal/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./pages/legal/PrivacyPolicy'));
const AccessibilityStatement = lazy(() => import('./pages/legal/AccessibilityStatement'));
const Sources = lazy(() => import('./pages/Sources'));
const ProfilePage = lazy(() => import('./pages/Profile'));
const Settings = lazy(() => import('./pages/Settings'));
const DataFlows = lazy(() => import('./pages/DataFlows'));
const ReferenceData = lazy(() => import('./pages/ReferenceData'));
const Presets = lazy(() => import('./pages/Presets'));
const DataFlowResult = lazy(() => import('./pages/DataFlowResult'));
const DataFlowSettings = lazy(() => import('./pages/DataFlowSettings'));
const Users = lazy(() => import('./pages/Users'));
const ServerStatus = lazy(() => import('./pages/ServerStatus'));
const AzureCallback = lazy(() => import('./pages/AzureCallback'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const Departments = lazy(() => import('./pages/Departments'));
const CreateDepartment = lazy(() => import('./pages/CreateDepartment'));
const DepartmentDetailPage = lazy(() => import('./pages/DepartmentDetailPage'));
const UpdateDepartment = lazy(() => import('./pages/UpdateDepartment'));
const AddUserToDepartment = lazy(() => import('./pages/AddUserToDepartment'));
const RemoveUserFromDepartment = lazy(() => import('./pages/RemoveUserFromDepartment'));
const DepartmentSettingsPage = lazy(() => import('./pages/DepartmentSettingsPage'));
const DepartmentPluginsPage = lazy(() => import('./pages/DepartmentPluginsPage'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const ChangePassword = lazy(() => import('./pages/ChangePassword'));
const NotFound = lazy(() => import('./pages/NotFound'));
const UnexpectedError = lazy(() => import('./pages/UnexpectedError'));
const BillingPage = lazy(() => import('./pages/BillingPage'));
const AdminBilling = lazy(() => import('./pages/AdminBilling'));
const AdminWorkspaces = lazy(() => import('./pages/AdminWorkspaces'));

function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}><Spinner size="large" /></div>}>
      {children}
    </Suspense>
  );
}

function lazyElement(Component: React.LazyExoticComponent<React.ComponentType>) {
  return <SuspenseWrapper><Component /></SuspenseWrapper>;
}

/**
 * Shared child routes for a data-flow (or legacy task) detail view.
 * Accepts `includeLegacyJobRoutes` to add the old `job/:actionId` aliases
 * that existed under the `task/*` path.
 */
function dataFlowDetailChildren({ includeLegacyJobRoutes = false } = {}) {
  const settingsChildren = [
    {
      index: true,
      element: lazyElement(DataFlowSettings)
    },
    ...(includeLegacyJobRoutes
      ? [{ path: 'job/:actionId', element: lazyElement(DataFlowSettings) }]
      : []),
    {
      path: 'action/:actionId',
      element: lazyElement(DataFlowSettings)
    }
  ];

  const resultChildren = [
    {
      index: true,
      element: lazyElement(DataFlowResult)
    },
    {
      path: ':resultId',
      children: [
        {
          index: true,
          element: lazyElement(DataFlowResult)
        },
        ...(includeLegacyJobRoutes
          ? [{ path: 'job/:actionId', element: lazyElement(DataFlowResult) }]
          : []),
        {
          path: 'action/:actionId',
          element: lazyElement(DataFlowResult)
        }
      ]
    }
  ];

  return [
    {
      path: ':id',
      children: [
        {
          path: 'settings',
          children: settingsChildren
        },
        {
          path: 'result',
          children: resultChildren
        }
      ]
    }
  ];
}

const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <SuspenseWrapper><UnexpectedError /></SuspenseWrapper>,
    id: 'root',
    children: [
      { index: true, element: <Navigate to="/home" replace /> },
      {
        path: 'home',
        element: lazyElement(Home)
      },
      {
        path: 'terms',
        element: lazyElement(TermsOfService)
      },
      {
        path: 'privacy',
        element: lazyElement(PrivacyPolicy)
      },
      {
        path: 'accessibility',
        element: lazyElement(AccessibilityStatement)
      },
      {
        path: 'data-flows',
        loader: checkAuthLoader,
        element: lazyElement(DataFlows)
      },
      {
        path: 'tasks',
        loader: checkAuthLoader,
        element: <Navigate to="/data-flows" replace />
      },
      {
        path: 'files',
        loader: checkAuthLoader,
        element: <Navigate to="/data-flows" replace />
      },
      {
        path: 'data-flow',
        loader: checkAuthLoader,
        children: dataFlowDetailChildren()
      },
      {
        // Legacy "task" path — same views as data-flow, plus old job/:actionId aliases
        path: 'task',
        loader: checkAuthLoader,
        children: dataFlowDetailChildren({ includeLegacyJobRoutes: true })
      },
      {
        path: 'match-ai-data',
        element: lazyElement(ReferenceData),
        loader: checkAuthLoader
      },
      {
        path: 'match-ai-data-points',
        loader: ({ request }) => {
          const authRedirect = checkAuthLoader();
          if (authRedirect) return authRedirect;
          const url = new URL(request.url);
          return new Response(null, {
            status: 302,
            headers: {
              Location: `/match-ai-data${url.search}${url.hash}`
            }
          });
        }
      },
      {
        path: 'lists',
        loader: checkAuthLoader,
        element: <Navigate to="/match-ai-data" replace />
      },
      {
        path: 'users',
        element: lazyElement(Users),
        loader: requireAdminConsole
      },
      {
        path: 'server-status',
        element: lazyElement(ServerStatus),
        loader: requireAdminConsole
      },
      {
        path: 'admin/billing',
        element: lazyElement(AdminBilling),
        loader: requirePlatformAdmin
      },
      {
        path: 'admin/workspaces',
        element: lazyElement(AdminWorkspaces),
        loader: requirePlatformAdmin
      },
      {
        // Legacy "organizations" path — redirected to "departments" for one release.
        path: 'organizations',
        loader: () => new Response(null, { status: 302, headers: { Location: '/departments' } })
      },
      {
        path: 'organizations/*',
        loader: ({ params }: { params: { '*'?: string } }) =>
          new Response(null, {
            status: 302,
            headers: { Location: `/departments/${params['*'] ?? ''}` }
          })
      },
      {
        // The backend grants a dept_admin nothing in this section, so it is
        // gated on edit_departments as a whole.
        path: 'departments',
        loader: requireEditDepartments,
        children: [
          {
            index: true,
            element: lazyElement(Departments)
          },
          {
            path: 'create',
            element: lazyElement(CreateDepartment),
            loader: requireEditDepartments
          },
          {
            path: ':id',
            children: [
              {
                index: true,
                element: lazyElement(DepartmentDetailPage)
              },
              {
                path: 'update',
                element: lazyElement(UpdateDepartment),
                loader: requireEditDepartments
              },
              {
                path: 'add-user',
                element: lazyElement(AddUserToDepartment),
                loader: requireEditDepartments
              },
              {
                path: 'remove-user',
                element: lazyElement(RemoveUserFromDepartment),
                loader: requireEditDepartments
              },
              {
                path: 'settings',
                element: lazyElement(DepartmentSettingsPage),
                loader: requireEditDepartments
              },
              {
                path: 'plugins',
                element: lazyElement(DepartmentPluginsPage),
                loader: requireEditDepartments
              }
            ]
          }
        ]
      },
      {
        path: 'presets',
        element: lazyElement(Presets),
        loader: checkAuthLoader
      },
      {
        path: 'recipes',
        loader: checkAuthLoader,
        element: <Navigate to="/presets" replace />
      },
      {
        path: 'sources',
        element: lazyElement(Sources),
        loader: checkAuthLoader
      },
      {
        path: 'data-stores',
        loader: checkAuthLoader,
        element: <Navigate to="/sources" replace />
      },
      {
        path: 'signin',
        element: lazyElement(Signin),
        loader: checkNotAuthLoader
      },
      {
        path: 'forgot-password',
        element: lazyElement(ForgotPassword),
        loader: checkNotAuthLoader
      },
      {
        path: 'reset-password',
        element: lazyElement(ResetPassword),
        loader: checkNotAuthLoader
      },
      {
        path: 'signup',
        element: lazyElement(Signup),
        loader: checkNotAuthLoader
      },
      {
        path: 'change-password',
        element: lazyElement(ChangePassword),
        loader: checkAuthLoader
      },
      {
        path: 'auth/forgot-password',
        element: lazyElement(ForgotPassword),
        loader: checkNotAuthLoader
      },
      {
        path: 'auth/reset-password',
        element: lazyElement(ResetPassword),
        loader: checkNotAuthLoader
      },
      {
        path: 'auth/change-password',
        element: lazyElement(ChangePassword),
        loader: checkAuthLoader
      },

      // Open to everyone: the emailed link must work whether or not the person is signed in.
      {
        path: 'auth/verify-email',
        element: lazyElement(VerifyEmail)
      },
      {
        path: 'verify-email',
        element: lazyElement(VerifyEmail)
      },

      {
        path: 'auth/azure/callback',
        element: lazyElement(AzureCallback)
      },
      {
        path: 'azure/callback',
        element: lazyElement(AzureCallback)
      },

      {
        path: 'profile',
        element: lazyElement(ProfilePage),
        loader: checkAuthLoader
      },
      {
        path: 'settings',
        loader: checkAuthLoader,
        children: [
          { index: true, element: lazyElement(Settings) },
          // TODO(A-12): move to a billing capability once the backend billing gate
          // (PR #57) lands. The billing views only authenticate today, so no
          // capability mirrors them; view_admin_console is the interim UI gate.
          { path: 'billing', element: lazyElement(BillingPage), loader: requireAdminConsole }
        ]
      }
    ]
  },
  { path: '*', element: lazyElement(NotFound) }
]);

export default router;
